/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { View, ScrollView, TextStyle } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import HeaderView, {
  HeaderStyle,
} from '@airasia-phoenix/react-native/widgets/HeaderView';
import {
  getColor,
  pd,
  getShape,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import MemoizedListView, {
  MemoizedListViewProps,
} from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import Check from '@move-travel/react-native-phoenix-icons/Check';

const NUM_COLUMNS = 3;

export interface LanguagesViewProps {
  onClose: () => void;
  title?: string;
  localeList: MemoizedListViewProps[];
  selectedLanguageValue: MemoizedListViewProps;
  onSelect: (item: MemoizedListViewProps) => void;
  testID?: string;
  showHeader?: boolean;
}

const LanguagesView = ({
  onClose,
  title,
  localeList,
  selectedLanguageValue,
  onSelect,
  showHeader = true,
}: LanguagesViewProps) => {
  const [selectedLanguageId, setSelectedLanguageId] = useState<string | null>(
    selectedLanguageValue?.id || null,
  );

  // Subscribe to the theme so colours re-resolve on light/dark toggle.
  const { theme } = useUnistyles();

  const selectedTextStyle = useMemo(
    () => ({
      ...(getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.sys.colour.secondary,
      ) as TextStyle),
      textDecorationLine: 'none' as const,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` re-triggers pd.sys.* colour resolution, which eslint can't see
    [theme],
  );

  const greenCheckIcon = useMemo(
    () => ({
      height: 24,
      width: 24,
      children: <Check />,
      tint: getColor(pd.sys.colour.secondary),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` re-triggers pd.sys.* colour resolution, which eslint can't see
    [theme],
  );

  const languageCodeStyle = useMemo(
    () => ({
      ...(getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.comp.list.primaryText.colour,
      ) as TextStyle),
      textDecorationLine: 'none' as const,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` re-triggers pd.sys.* colour resolution, which eslint can't see
    [theme],
  );

  const getRowContainerStyle = useCallback(
    (id: string) => [
      getShape(pd.sys.shape.corner.small),
      id === selectedLanguageId && {
        boxShadow: [
          {
            inset: true,
            offsetX: 0,
            offsetY: 0,
            spreadDistance: 2,
            color: getColor(pd.sys.colour.secondary),
          },
        ],
        backgroundColor: getColor(pd.sys.colour.secondary_low),
      },
    ],
    [selectedLanguageId],
  );

  const handleLanguageSelect = useCallback(
    (item: MemoizedListViewProps) => {
      setSelectedLanguageId(item.id);
      onSelect(item);
    },
    [onSelect],
  );

  const chunkArray = useCallback(<T,>(arr: T[], size: number): T[][] => {
    return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size),
    );
  }, []);

  return (
    <View
      style={[
        { backgroundColor: getColor(pd.sys.colour.container_none) },
        styles.modalContainer,
      ]}
    >
      {showHeader && (
        <HeaderView
          headerStyle={HeaderStyle.IMMERSE}
          startIcon={{
            icon: { icon: pd.icon.main.navigation.close },
            onPress: onClose,
          }}
          titleStart={title}
        />
      )}

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View>
          {chunkArray(localeList, NUM_COLUMNS).map((row, rowIndex) => (
            <View key={rowIndex} style={styles.row}>
              {row.map((item) => {
                const [languageCode] = item.primaryText.split(/\s[–-]\s/);
                return (
                  <View key={item.id} style={styles.column}>
                    <MemoizedListView
                      id={item.id}
                      primaryText={item.primaryText}
                      primaryHighlightText={
                        languageCode
                          ? [
                              {
                                spannable: languageCode,
                                style: languageCodeStyle,
                              },
                            ]
                          : []
                      }
                      memoizedOnPress={() => handleLanguageSelect(item)}
                      containerStyle={getRowContainerStyle(item.id)}
                      endElement={
                        item.id === selectedLanguageId
                          ? {
                              iconViewProps: greenCheckIcon,
                              containerStyle: { marginTop: 0 },
                            }
                          : undefined
                      }
                      primaryTextStyle={
                        item.id === selectedLanguageId
                          ? selectedTextStyle
                          : undefined
                      }
                      model={{ id: item.id }}
                    />
                  </View>
                );
              })}
              {Array.from({ length: NUM_COLUMNS - row.length }).map(
                (_, index) => (
                  <View
                    key={`placeholder-${index}`}
                    style={styles.column}
                    testID="language-grid-placeholder"
                  />
                ),
              )}
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create((pd) => ({
  modalContainer: {
    overflow: 'hidden',
    elevation: 2,
    paddingBottom: pd.ref.space.spacing40.px,
  },
  heading: {
    marginBottom: pd.ref.space.spacing20.px,
    paddingLeft: pd.ref.space.spacing40.px,
  },
  scrollContent: {
    flexGrow: 1,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: pd.ref.space.spacing20.px,
    flexWrap: 'wrap',
    gap: pd.ref.space.spacing40.px,
  },
  column: {
    flex: 1,
  },
}));

export default LanguagesView;
